import { useCallback, useEffect, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { TAB_KEYS, TAB_LISTS } from "./config/user-management";
import { Tab, Nav } from "react-bootstrap";
import FullPageLoader from "../common/FullPageLoader";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Pagination from "../common/Pagination";
import UserManagementTable from "./UserManagementTable";
import CreateUserModal from "./CreateUserModal";
import { executeGraphQLQuery } from "../../common/executeGraphQLQuery";
import { GET_USERS_LIST } from "../../services/user.gql";

const UserManagement = () => {
  const client = useApolloClient();
  const { t } = useTranslation("common");
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(TAB_KEYS.Active);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({});
  const [listPayload, setListPayload] = useState({
    page_number: 1,
    status: currentTab === TAB_KEYS.Active ? 1 : 0,
    search: null,
  });
  const [tabsCount, setTabsCount] = useState(
    Object.values(TAB_KEYS).reduce(
      (returnObj, key) => ({ ...returnObj, [key]: 0 }),
      {}
    )
  );

  const getUsersList = useCallback(() => {
    setLoading(true);
    executeGraphQLQuery(GET_USERS_LIST(), client, {
      getUsersListInput: listPayload,
    })
      .then((response) => {
        if (response?.errors?.length) {
          throw response.errors[0];
        } else if (!response?.data?.getUsersList) {
          throw new Error("Something went wrong!");
        }
        setUsers(response.data.getUsersList?.rows || []);
        setPagination(response.data.getUsersList?.pagination || {});
        setTabsCount(response.data.getUsersList?.tabsCount);
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [client, listPayload]);

  useEffect(() => {
    setListPayload((prev) => ({
      ...prev,
      page_number: 1,
      status: currentTab === TAB_KEYS.Active ? 1 : 0,
    }));
  }, [currentTab]);

  useEffect(() => {
    getUsersList();
  }, [getUsersList]);

  function handleTabSelect(eventKey) {
    setCurrentTab(eventKey);
  }

  function handleAddUserClick() {
    if (showAddUserModal) {
      document.body.classList.remove("overflow-hidden");
    } else {
      document.body.classList.add("overflow-hidden");
    }
    setShowAddUserModal((prev) => !prev);
  }

  function handleNextPageClick(params) {
    console.log(params);
    setListPayload((prev) => ({ ...prev, page_number: params.page_no }));
  }

  return (
    <>
      <FullPageLoader show={loading} />
      <div className="container-fluid">
        <Tab.Container
          defaultActiveKey={TAB_KEYS.ALL}
          onSelect={handleTabSelect}
        >
          <div className="top-heading">
            <h1>Sales Support User Management</h1>
          </div>
          <div className="tab-line white-bg p-l-r-15 minus-l-r-15 hading-tab-top p-md-t">
            <Nav variant="pills" className="flex-column">
              <div className="tab-list">
                {TAB_LISTS.map((tab) => (
                  <Nav.Item key={tab.key}>
                    <Nav.Link
                      eventKey={tab.key}
                      active={tab.isActive(currentTab)}
                    >
                      {t(tab.value)} <span>({tab.count(tabsCount)})</span>
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </div>
            </Nav>

            <div className="d-flex">
              <fieldset className="search-bx">
                <div className="material">
                  <input
                    id="search_by"
                    className="form-input"
                    type="text"
                    name="search_by"
                    placeholder="Search By Name Or Email"
                    autocomplete="off"
                    value={listPayload.search || ""}
                    onChange={(e) =>
                      setListPayload((prev) => ({
                        ...prev,
                        search: e.target.value,
                        page_number: 1,
                      }))
                    }
                  />
                  <i className="ic-search"></i>
                </div>
              </fieldset>
              <button
                onClick={handleAddUserClick}
                className="btn-line btn-add-incentive"
              >
                <i className="ic-add"></i>
                <span>{t("USER_MANAGEMENT.ADD_USER")}</span>
              </button>
            </div>
          </div>
        </Tab.Container>
      </div>
      <UserManagementTable
        currentTab={currentTab}
        loading={loading}
        setLoading={setLoading}
        users={users}
        getUsersList={getUsersList}
      />
      {showAddUserModal ? (
        <CreateUserModal
          showAddUserModal={showAddUserModal}
          handleAddUserClick={handleAddUserClick}
          getUsersList={getUsersList}
        />
      ) : null}
      <div className="pagination-outer">
        {!loading ? (
          <Pagination
            paginateData={{
              page: 1,
              runPaginationFunc: () => {},
              loading,
              tableData: users,
              totalLengthWithoutPagination: 0,
              paginationData: pagination,
            }}
            sendRequestDataToParent={handleNextPageClick}
          />
        ) : null}
      </div>
    </>
  );
};

export default UserManagement;
